/**
 * Demand counts and user seats are unlimited.
 * Paid file storage is sold separately via Evidence Locker.
 */

export const ARCHIVE_STORAGE_CURRENCY = 'USD';

/**
 * Legacy single "included" entitlement. Demand plans are no longer sold;
 * active/archive/seats have no caps or monthly price.
 */
export const ARCHIVE_STORAGE_TIERS = [
  {
    id: 'free',
    label: 'Included',
    activeLimit: null,
    archiveLimit: null,
    seatLimit: null,
    extraSeatPrice: null,
    limit: null,
    monthlyPrice: 0,
    stripePriceId: null,
    description:
      'Unlimited active and archived demands, and unlimited user seats. Evidence Locker is billed separately by storage.',
  },
];

/** @deprecated Archive is unlimited. */
export const FREE_ARCHIVE_LIMIT = Number.POSITIVE_INFINITY;
/** @deprecated Active demands are unlimited. */
export const FREE_ACTIVE_LIMIT = Number.POSITIVE_INFINITY;
/** @deprecated Demand plans are no longer sold. */
export const ARCHIVE_STORAGE_MONTHLY_PRICE = 0;
/** @deprecated Demand plans are no longer sold. */
export const ARCHIVE_STORAGE_STRIPE_PRICE_ID = null;

export function getArchiveTiers() {
  return ARCHIVE_STORAGE_TIERS.map((tier) => ({ ...tier }));
}

export function getArchiveTiersById(tierId) {
  const id = String(tierId || 'free').toLowerCase();
  return ARCHIVE_STORAGE_TIERS.find((tier) => tier.id === id) || ARCHIVE_STORAGE_TIERS[0];
}

export function getArchiveTiersAbove() {
  return [];
}

export function resolveArchivePlanId() {
  return 'free';
}

function baseEntitlement(billing = {}) {
  return {
    planId: 'free',
    tierId: 'free',
    tierLabel: 'Included',
    unlimited: true,
    enabled: false,
    monthlyPrice: 0,
    currency: ARCHIVE_STORAGE_CURRENCY,
    stripePriceId: null,
    stripeSubscriptionItemId: billing?.archiveStorageStripeItemId || null,
    description: ARCHIVE_STORAGE_TIERS[0].description,
    activeLimit: Number.POSITIVE_INFINITY,
    archiveLimit: Number.POSITIVE_INFINITY,
    includedSeatLimit: Number.POSITIVE_INFINITY,
    extraSeatPrice: null,
    extraSeats: 0,
    seatLimit: Number.POSITIVE_INFINITY,
  };
}

export function getArchiveEntitlement(billing = {}) {
  const base = baseEntitlement(billing);
  return {
    ...base,
    freeLimit: Number.POSITIVE_INFINITY,
    limit: Number.POSITIVE_INFINITY,
    unlimited: true,
  };
}

export function getActiveEntitlement(billing = {}) {
  const base = baseEntitlement(billing);
  return {
    ...base,
    freeLimit: Number.POSITIVE_INFINITY,
    limit: Number.POSITIVE_INFINITY,
    unlimited: true,
  };
}

export function getSeatEntitlement(billing = {}) {
  const base = baseEntitlement(billing);
  return {
    ...base,
    freeLimit: Number.POSITIVE_INFINITY,
    limit: Number.POSITIVE_INFINITY,
    unlimited: true,
    allowsExtraSeats: false,
  };
}

export function buildArchiveUsage({ used = 0, billing = {} } = {}) {
  const entitlement = getArchiveEntitlement(billing);
  const usedCount = Math.max(0, Number(used) || 0);

  return {
    ...entitlement,
    used: usedCount,
    remaining: Number.POSITIVE_INFINITY,
    atLimit: false,
    overLimit: false,
    nextTier: null,
    upgradeOptions: [],
    label: `${usedCount} archived demands · unlimited`,
  };
}

export function buildActiveUsage({ used = 0, billing = {} } = {}) {
  const entitlement = getActiveEntitlement(billing);
  const usedCount = Math.max(0, Number(used) || 0);

  return {
    ...entitlement,
    used: usedCount,
    remaining: Number.POSITIVE_INFINITY,
    atLimit: false,
    overLimit: false,
    nextTier: null,
    upgradeOptions: [],
    label: `${usedCount} active demands · unlimited`,
  };
}

export function buildSeatUsage({ used = 0, billing = {} } = {}) {
  const entitlement = getSeatEntitlement(billing);
  const usedCount = Math.max(0, Number(used) || 0);

  return {
    ...entitlement,
    used: usedCount,
    remaining: Number.POSITIVE_INFINITY,
    atLimit: false,
    overLimit: false,
    nextTier: null,
    upgradeOptions: [],
    extraSeatMonthly: 0,
    label: `${usedCount} seats · unlimited`,
  };
}

/** Archived demands are unlimited. */
export function canArchiveMore({ used = 0, addCount = 0, billing = {} } = {}) {
  const usage = buildArchiveUsage({ used, billing });
  const add = Math.max(0, Number(addCount) || 0);
  return { ok: true, usage, remaining: Number.POSITIVE_INFINITY, accepted: add };
}

/** Active demands are unlimited. */
export function canCreateActiveDemands({ used = 0, addCount = 0, billing = {} } = {}) {
  const usage = buildActiveUsage({ used, billing });
  const add = Math.max(0, Number(addCount) || 0);
  return { ok: true, usage, remaining: Number.POSITIVE_INFINITY, accepted: add };
}

/** User seats are unlimited. */
export function canInviteMoreUsers({ used = 0, addCount = 0, billing = {} } = {}) {
  const usage = buildSeatUsage({ used, billing });
  const add = Math.max(0, Number(addCount) || 0);
  return { ok: true, usage, remaining: Number.POSITIVE_INFINITY, accepted: add };
}

export function activeLimitMessage() {
  return 'Active demands are unlimited on every account.';
}

export function seatLimitMessage() {
  return 'User seats are unlimited on every account.';
}

/** @deprecated Demand plans are no longer sold; forces included entitlement. */
export function setArchiveStoragePlan(billing = {}, planId = 'free', extras = {}) {
  return {
    ...billing,
    archiveStoragePlanId: 'free',
    archiveFreeLimit: null,
    activeDemandFreeLimit: null,
    seatFreeLimit: null,
    archiveStorageEnabled: false,
    archiveStorageEnabledAt: null,
    archiveStorageStripePriceId: null,
    archiveStorageStripeItemId: extras.stripeItemId || billing.archiveStorageStripeItemId || null,
    archiveStorageStripeSubscriptionId:
      extras.stripeSubscriptionId || billing.archiveStorageStripeSubscriptionId || null,
    extraSeats: 0,
  };
}

/** @deprecated Extra seats are unused; seats are unlimited. */
export function setExtraSeats(billing = {}) {
  return { ...billing, extraSeats: 0 };
}

/** @deprecated Prefer setArchiveStoragePlan(billing, 'free'). */
export function enableArchiveStorageBilling(billing = {}, extras = {}) {
  return setArchiveStoragePlan(billing, 'free', extras);
}

/** @deprecated Prefer setArchiveStoragePlan(billing, 'free'). */
export function disableArchiveStorageBilling(billing = {}) {
  return setArchiveStoragePlan(billing, 'free');
}
